import { useState, useEffect } from 'react';

export default function MeetingPrompts({ template, startTime, isHost }) {
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [promptsEnabled, setPromptsEnabled] = useState(() => {
    const saved = localStorage.getItem('quickmeet_prompts_enabled');
    return saved !== null ? saved === 'true' : true; // Default enabled
  });
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  // Don't show prompts if:
  // - Not the host
  // - No template
  // - Prompts disabled
  if (!isHost || !template || !promptsEnabled) {
    return null;
  }

  const { agenda, duration, name, tips } = template;

  // Calculate time intervals for each agenda item
  const intervals = agenda.map((item, index) => {
    const intervalDuration = duration / agenda.length;
    return {
      item,
      startMinute: Math.floor(intervalDuration * index),
      endMinute: Math.floor(intervalDuration * (index + 1))
    };
  });

  // Update elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000 / 60);
      setElapsedMinutes(elapsed);
    }, 1000); // Update every second for smooth transitions

    return () => clearInterval(interval);
  }, [startTime]);

  // Update current prompt based on elapsed time
  useEffect(() => {
    const currentInterval = intervals.find(
      interval => elapsedMinutes >= interval.startMinute && elapsedMinutes < interval.endMinute
    );

    if (currentInterval && currentInterval.item !== currentPrompt?.item) {
      setCurrentPrompt(currentInterval);
    }
  }, [elapsedMinutes, intervals]);

  // Toggle prompts
  const togglePrompts = (enabled) => {
    setPromptsEnabled(enabled);
    localStorage.setItem('quickmeet_prompts_enabled', enabled.toString());
  };

  return (
    <>
      {/* Settings Button */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        className="fixed bottom-24 right-4 z-40 p-3 bg-white dark:bg-[#2c2c2e] border-2 border-[#06c] rounded-full shadow-lg hover:scale-110 transition-all"
        title="Meeting Prompts Settings"
      >
        <svg className="w-5 h-5 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      </button>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">
                Meeting Prompts
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] rounded-lg transition"
              >
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-[#6e6e73] dark:text-[#8e8e93] text-sm mb-4">
              Get guided prompts for your <span className="font-semibold">{name}</span> meeting agenda.
            </p>

            {/* Toggle */}
            <div className="mb-4">
              <label className="flex items-center justify-between cursor-pointer p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all">
                <div>
                  <p className="font-medium text-[#1d1d1f] dark:text-white">Enable Prompts</p>
                  <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">Show agenda reminders during meeting</p>
                </div>
                <input
                  type="checkbox"
                  checked={promptsEnabled}
                  onChange={(e) => togglePrompts(e.target.checked)}
                  className="w-12 h-6 rounded-full appearance-none bg-[#d2d2d7] relative cursor-pointer transition-colors checked:bg-[#06c] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-2"
                  style={{
                    backgroundImage: promptsEnabled
                      ? 'radial-gradient(circle at 70% 50%, white 40%, transparent 40%)'
                      : 'radial-gradient(circle at 30% 50%, white 40%, transparent 40%)'
                  }}
                />
              </label>
            </div>

            {/* Agenda Preview */}
            <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-4">
              <h4 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-2">
                Agenda ({duration} min)
              </h4>
              <div className="space-y-2">
                {intervals.map((interval, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2 text-sm ${
                      currentPrompt?.item === interval.item
                        ? 'text-[#06c] font-semibold'
                        : 'text-[#6e6e73] dark:text-[#8e8e93]'
                    }`}
                  >
                    <span className="text-xs mt-0.5 font-mono">
                      {interval.startMinute}min
                    </span>
                    <span>{interval.item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            {tips && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-blue-900 dark:text-blue-200">{tips}</p>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowSettings(false)}
              className="mt-4 w-full px-4 py-2.5 bg-[#06c] text-white rounded-xl font-medium hover:bg-[#0077ed] transition-all"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Current Prompt Overlay */}
      {currentPrompt && promptsEnabled && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 animate-slide-down max-w-xl w-full px-4">
          <div className="bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-2xl shadow-2xl p-4 border-2 border-white/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-xl">{template.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold text-sm">
                    {name} - Stage {intervals.findIndex(i => i.item === currentPrompt.item) + 1}/{intervals.length}
                  </h4>
                  <span className="text-xs text-white/80 font-mono">
                    {currentPrompt.startMinute}-{currentPrompt.endMinute} min
                  </span>
                </div>
                <p className="text-base font-medium">{currentPrompt.item}</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full transition-all duration-1000"
                      style={{
                        width: `${((elapsedMinutes - currentPrompt.startMinute) / (currentPrompt.endMinute - currentPrompt.startMinute)) * 100}%`
                      }}
                    />
                  </div>
                  <span className="text-xs text-white/80">
                    {Math.max(0, currentPrompt.endMinute - elapsedMinutes)} min left
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
